import { Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { endpoints } from '../../../.conf/resources';
import { getTenant } from '../../helpers/helpers';
import type { ResponsiveVideos } from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-moving-image',
  styleUrl: 'cms-moving-image.scss',
  shadow: true,
})
export class CmsMovingImage {
  private readonly bgImage = 'scratch-bg-brand1.webp';
  private readonly imageSrc = `${endpoints.assetPath}/${this.bgImage}`;

  private responsiveVideos: ResponsiveVideos;
  private videoContainer: HTMLDivElement;

  private videoBreakpoints = {
    desktop: 1280,
    tablet: 720,
    mobile: 480,
    current: 0,
  };

  private videoSources = {
    mp4: null,
    webm: null,
  };

  /**
   * set the Responsive Videos in mp4 and webm format
   */
  @Prop({ mutable: true }) videos!: ResponsiveVideos | string;
  /**
   * Set the Headline
   */
  @Prop({ mutable: true }) headline: string;
  /**
   *Set the subheading
   */
  @Prop({ mutable: true }) subheading: string;
  /**
   * Set the target URL for the Video Link
   */
  @Prop({ mutable: true }) targetUrl: string;
  /**
   * Set the target tab for the Video Link like _self or _blank
   */
  @Prop({ mutable: true }) targetWindow: string;
  /**
   * Set the Background Color for use in Breakpoint >= 1920px
   */
  @Prop({ mutable: true }) backgroundColor: string;

  /**
   * Set the tag for the headline
   */
  @Prop({ reflect: true }) headlineTag:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6' = 'h2';

  @Watch('videos')
  private videosWatcher(newValue: ResponsiveVideos | string) {
    if (newValue) {
      this.responsiveVideos =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Element() videoNode: HTMLElement;

  private renderHeadline() {
    return (
      <div class={'headline-wrapper'}>
        <div
          class={'headline-wrapper-inner'}
          style={
            getTenant() === 'hornbach'
              ? { backgroundImage: `url(${this.imageSrc})` }
              : {
                  backgroundColor:
                    'var(--cms-semanticColors-primary-background)',
                }
          }
        >
          {this.headline !== undefined ? (
            <cms-headline
              style={{
                '--cms-grid-padding': '0',
                '--cms-spacing-top': '0',
              }}
              isAdaptive={true}
              headlineText={this.headline}
              headlineTag={this.headlineTag}
              headlineClass='moving-image-headline'
              colorToken={'--cms-semanticColors-primary-textOnBackground'}
            />
          ) : null}
          {this.subheading !== undefined ? (
            <div class={'moving-image-subheading'}>
              {this.subheading}
              <span class={'cta-svg'}>
                {this.targetUrl ? (
                  <cms-icon
                    size='xl'
                    colorToken={'--cms-semanticColors-primary-iconOnBackground'}
                    icon='arrowShaftRight'
                  />
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  private renderContent() {
    return (
      <div class={'moving-image-content'}>
        {this.responsiveVideos !== undefined ? this.renderVideoHtml() : null}
        {this.headline !== undefined || this.subheading !== undefined
          ? this.renderHeadline()
          : null}
      </div>
    );
  }

  private setVideoSrc(src: string, type: string) {
    if (type !== 'mp4' && type !== 'webm') {
      return;
    }

    if (type === 'mp4') {
      this.videoSources.mp4 = src || null;
    } else {
      this.videoSources.webm = src || null;
    }
  }

  private getVideosIsMatchingBreakpoint(responsiveVideos: ResponsiveVideos) {
    if (!responsiveVideos) {
      return;
    }

    const matchVideoBreakpoint = this.checkVideoIsMatchingBreakpoint(
      responsiveVideos,
    )
      ? this.videoBreakpoints.current
      : 720;

    this.videoSources.mp4 = null;
    this.videoSources.webm = null;

    for (const [key, videoSrc] of Object.entries(responsiveVideos)) {
      const [videoSize, type] = key.split('-');

      if (Number.parseInt(videoSize) === matchVideoBreakpoint) {
        this.setVideoSrc(videoSrc, type);
      }
    }

    return this.videoSources;
  }

  private generateVideo(sources: { mp4: string | null; webm: string | null }) {
    const videoElement = document.createElement('video');
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.preload = 'metadata';
    videoElement.classList.add('moving-image-video');

    if (sources.webm) {
      const webmSource = document.createElement('source');
      webmSource.src = sources.webm;
      webmSource.type = 'video/webm';
      videoElement.appendChild(webmSource);
    }

    if (sources.mp4) {
      const mp4Source = document.createElement('source');
      mp4Source.src = sources.mp4;
      mp4Source.type = 'video/mp4';
      videoElement.appendChild(mp4Source);
    }

    const trackElement = document.createElement('track');
    trackElement.default = true;
    trackElement.kind = 'captions';
    trackElement.srclang = 'de';
    videoElement.appendChild(trackElement);

    return videoElement;
  }

  private renderVideoHtml() {
    return <div class={'video-container'} />;
  }

  private setCurrentBreakpoint() {
    const windowViewWidth = window.innerWidth;
    if (windowViewWidth >= this.videoBreakpoints.desktop) {
      return 1280;
    }
    return windowViewWidth >= this.videoBreakpoints.tablet ? 720 : 480;
  }

  private checkVideoIsMatchingBreakpoint(responsiveVideos: ResponsiveVideos) {
    const searchKeyMp4 = `${this.videoBreakpoints.current}-mp4`;
    const searchKeyWebm = `${this.videoBreakpoints.current}-webm`;

    return (
      Object.hasOwn(responsiveVideos, searchKeyMp4) ||
      Object.hasOwn(responsiveVideos, searchKeyWebm)
    );
  }

  connectedCallback(): void {
    if (this.videos) {
      this.videosWatcher(this.videos);
    }

    this.videoBreakpoints = {
      ...this.videoBreakpoints,
      current: this.setCurrentBreakpoint(),
    };
  }

  componentDidLoad() {
    this.videoContainer =
      this.videoNode.shadowRoot.querySelector('.video-container');

    const sources = this.getVideosIsMatchingBreakpoint(this.responsiveVideos);
    const videoNode = this.generateVideo(sources);
    this.videoContainer.innerHTML = '';
    this.videoContainer.insertBefore(videoNode, this.videoContainer.firstChild);
  }

  render() {
    return (
      <Host
        class={'moving-image'}
        style={
          this.backgroundColor
            ? { background: `var(${this.backgroundColor})` }
            : null
        }
      >
        <div class={'moving-image-container'}>
          {this.targetUrl !== undefined ? (
            <a
              href={this.targetUrl}
              class={'moving-image-link'}
              title={this.headline ? this.headline : ''}
              target={this.targetWindow ? this.targetWindow : '_blank'}
            >
              {this.renderContent()}
            </a>
          ) : (
            this.renderContent()
          )}
        </div>
      </Host>
    );
  }
}
